/**
 * 世界配置编辑器
 * 支持世界全局设置、时代背景、游戏规则等配置
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  message,
  Alert,
} from 'antd'
import {
  GlobalOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  HistoryOutlined,
  SoundOutlined,
} from '@ant-design/icons'

const { TextArea } = Input
const { Text, Title } = Typography
const { TabPane } = Tabs
const { Option } = Select

// 背景时代选项
const ERA_OPTIONS = [
  { value: 'modern', label: '现代' },
  { value: 'ww2', label: '二战时期' },
  { value: 'cold_war', label: '冷战时期' },
  { value: 'future', label: '未来科幻' },
  { value: 'fantasy', label: '奇幻' },
  { value: 'ancient', label: '古代' },
]

// 氛围选项
const ATMOSPHERE_OPTIONS = [
  { value: 'peaceful', label: '和平' },
  { value: 'tense', label: '紧张' },
  { value: 'mysterious', label: '神秘' },
  { value: 'horror', label: '恐怖' },
  { value: 'romantic', label: '浪漫' },
  { value: 'adventure', label: '冒险' },
]

interface WorldEditorFormProps {
  initialData?: Record<string, any>
  onSave: (data: Record<string, any>) => Promise<void>
  loading?: boolean
}

const WorldEditorForm: React.FC<WorldEditorFormProps> = ({
  initialData,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)
  // 保存原始tags对象数组的引用
  const [originalTags, setOriginalTags] = useState<any[]>([])

  useEffect(() => {
    if (initialData) {
      // 保存原始tags对象数组
      const tagsArray = initialData.tags || []
      setOriginalTags(tagsArray)

      // 提取tag名称用于显示
      const tagNames = Array.isArray(tagsArray)
        ? tagsArray.map((tag: any) => typeof tag === 'object' && tag.name ? tag.name : tag)
        : []

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        setting: initialData.setting,
        era: initialData.era,
        atmosphere: initialData.atmosphere,
        tags: tagNames, // 使用name数组而不是对象数组
        player_template: initialData.player_template || {},
        rules: typeof initialData.rules === 'string'
          ? initialData.rules
          : JSON.stringify(initialData.rules || {}, null, 2),
        audio: initialData.audio || {},
      })
    } else {
      setOriginalTags([])
      form.resetFields()
      form.setFieldsValue({
        tags: [],
        player_template: {
          health: 100,
          morale: 100,
          stamina: 100,
          wealth: 0,
          inventory: [],
          skills: [],
        },
        rules: JSON.stringify({
          death: {
            enabled: true,
            respawn: true,
            respawn_time: 30,
            penalty: 'lose_evidence'
          },
          victory: {
            conditions: []
          },
          defeat: {
            conditions: []
          }
        }, null, 2),
        audio: {
          bgm: '',
          ambient: '',
          volume: {
            bgm: 50,
            sfx: 80,
            voice: 100
          }
        }
      })
    }
  }, [initialData, form])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 解析 rules JSON
      let rulesData = {}
      if (values.rules) {
        try {
          rulesData = typeof values.rules === 'string' ? JSON.parse(values.rules) : values.rules
        } catch (e) {
          message.error('游戏规则 JSON 格式错误')
          setSaving(false)
          return
        }
      }

      // 处理 tags：将用户输入的标签名称映射回完整的 tag 对象
      let finalTags: any[] = []
      if (Array.isArray(values.tags) && values.tags.length > 0) {
        // 如果有原始 tags，保留用户编辑后的名称对应的完整对象
        if (originalTags.length > 0) {
          const originalTagsMap = new Map(
            originalTags.map((tag: any) => [tag.name, tag])
          )
          finalTags = values.tags
            .map((tagName: string) => originalTagsMap.get(tagName))
            .filter(Boolean) // 过滤掉 undefined
        } else {
          // 新建世界时，用户输入的是简单字符串，直接使用
          finalTags = values.tags
        }
      }

      const worldData = {
        key: values.key,
        name: values.name,
        description: values.description,
        setting: values.setting,
        era: values.era,
        atmosphere: values.atmosphere,
        tags: finalTags,
        rules: rulesData,
        player_template: values.player_template || {},
        audio: values.audio || {},
      }

      await onSave(worldData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <GlobalOutlined style={{ color: '#1890ff' }} />
          <span>{initialData?.name || '新建世界'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="世界键" rules={[{ required: true }]}>
                  <Input placeholder="例如: world_51_station" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="世界名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 51号兵站" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="era" label="时代背景">
                  <Select placeholder="选择时代" options={ERA_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="世界的详细描述..." />
            </Form.Item>

            <Form.Item name="setting" label="背景设定">
              <TextArea
                rows={4}
                placeholder="故事的背景设定，如：1941年的上海滩，日军占领下的孤岛..."
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="atmosphere" label="氛围">
                  <Select placeholder="选择氛围" options={ATMOSPHERE_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="tags" label="标签 (逗号分隔)">
                  <Select mode="tags" placeholder="mystery, adventure, horror" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="玩家模板" key="player">
            <Alert
              message="玩家模板配置"
              description="新玩家进入游戏时的初始状态。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name={['player_template', 'health']} label="初始生命值">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="100" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['player_template', 'morale']} label="初始士气">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="100" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['player_template', 'stamina']} label="初始体力">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="100" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['player_template', 'wealth']} label="初始财富">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Form.Item name={['player_template', 'inventory']} label="初始物品">
              <Select
                mode="tags"
                placeholder="item_medicine, item_food"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name={['player_template', 'skills']} label="初始技能">
              <Select
                mode="tags"
                placeholder="observation, lockpicking"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="游戏规则" key="rules">
            <Alert
              message="游戏规则配置"
              description="定义游戏的基本规则，如死亡机制、胜负条件等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="rules" label="游戏规则 (JSON)">
              <TextArea
                rows={12}
                placeholder={`{
  "death": {
    "enabled": true,
    "respawn": true,
    "respawn_time": 30,
    "penalty": "lose_evidence"
  },
  "victory": {
    "conditions": [
      {"condition": "sw_evidence_delivered"},
      {"condition": "sw_survived"}
    ]
  },
  "defeat": {
    "conditions": [
      {"condition": "var_health <= 0"},
      {"condition": "sw_caught_by_enemy"}
    ]
  },
  "time_limit": {
    "enabled": true,
    "duration": 6300,
    "overtime": "game_over"
  }
}`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="音效配置" key="audio">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['audio', 'bgm']} label="背景音乐">
                  <Input placeholder="music/background/ambient.mp3" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['audio', 'ambient']} label="环境音效">
                  <Input placeholder="sounds/ambient/rain.mp3" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Form.Item label="音量配置">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={['audio', 'volume', 'bgm']} label="BGM音量">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="50" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['audio', 'volume', 'sfx']} label="音效音量">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="80" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['audio', 'volume', 'voice']} label="语音音量">
                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="100" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

export default WorldEditorForm
